import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

export interface INocOnlineCourse {
  id: number;
  referenceNumber: string;
  empId: string;
  approverUserID: string;
  onlineCourseName: string;
  courseFee: string;
  onlineCourseCategory: string;
  onlineCourseDepartment?: string;
  requestCreationDate: string | null;
  requestStatus: string | null;
  is_draft?: boolean;
  approverName: string;
  remarks: string;
  hRemarks?: string;
  created_at: string;
  modified_at?: string;
  deleted_at?: string | null;
  course_category: {
    category_id: string;
    category_title: string;
    course_departments?: any;
    [key: string]: any;
  } | any;
  // Additional fields from entity
  empID?: string;
  employee?: any;
  documents?: any[];
  approver?: any;
  currency?: string;
  durationOfOnlineCourse?: string;
  dateOfCourseJoining?: string | Date | null;
  courseEndingDate?: string | Date | null;
  lastDateOfRegistration?: string | Date | null;
  whetherCurrentlyPur?: string;
  whether2YearsRegu?: string;
  whetherApprovalRec?: string;
  whetherAnotherNoc?: string;
  natureOfDutyPerfo?: string;
  nature_of_duty?: any;
  isReleventToEmployee?: string;
  keyForCourse?: string;
  courseBrochure?: string;
}

export interface ICourseDepartment {
  department: string;
}

interface INocOnlineCoursesState {
  data: INocOnlineCourse[];
  currentCourse: INocOnlineCourse | null;
  courseDepartments: ICourseDepartment[];
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  deleteLoading: boolean;
  courseDepartmentsLoading: boolean;
  total: number;
  page: number;
  limit: number;
}

const initialState: INocOnlineCoursesState = {
  data: [],
  currentCourse: null,
  courseDepartments: [],
  loading: false,
  error: null,
  createLoading: false,
  deleteLoading: false,
  courseDepartmentsLoading: false,
  total: 0,
  page: 1,
  limit: 10,
};

// Fetch all online courses
export const getAllNocOnlineCourses = createAsyncThunk(
  "nocOnlineCourses/getAll",
  async (
    payload: {
      query?: string;
      page?: number;
      limit?: number;
      orderColumn?: string;
      orderBy?: string;
      requestStatus?: string;
      startDate?: string;
      endDate?: string;
    },
    thunkAPI
  ) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.FIND_ALL_ONLINE_COURSES,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Fetch course departments
export const getCourseDepartments = createAsyncThunk(
  "nocOnlineCourses/getCourseDepartments",
  async (payload: any = {}, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.MASTERS.COURSE_DEPARTMENT_LIST,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Fetch single online course
export const getNocOnlineCourse = createAsyncThunk(
  "nocOnlineCourses/getOne",
  async (payload: { id: number }, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.FIND_ONE_ONLINE_COURSE,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Create or update online course
export const createOrUpdateNocOnlineCourse = createAsyncThunk(
  "nocOnlineCourses/createOrUpdate",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.CREATE_OR_UPDATE_ONLINE_COURSE,
        payload
      );
      return response.data;
    } catch (error: any) {
      console.log("error", error);
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Delete online course
export const deleteNOCOnlineCourseRequest = createAsyncThunk(
  "nocOnlineCourses/delete",
  async (payload: { id: number }, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.DELETE_ONLINE_COURSE,
        payload
      );
      return { ...response.data, deletedId: payload.id };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Approve/Reject online course
export const approveRejectNocOnlineCourse = createAsyncThunk(
  "nocOnlineCourses/approveReject",
  async (
    payload: {
      Action?: string;
      Approve_Reject_flag?: string;
      ReferenceNumber: string;
      Remarks?: string;
      remarks?: string;
      HRemarks?: string;
      hRemarks?: string;
    },
    thunkAPI
  ) => {
    try {
      // Support both Action and Approve_Reject_flag for backward compatibility
      const requestPayload = {
        Approve_Reject_flag: payload.Action || payload.Approve_Reject_flag,
        ReferenceNumber: payload.ReferenceNumber,
        remarks: payload.Remarks || payload.remarks || "",
        hRemarks: payload.HRemarks || payload.hRemarks || "",
      };

      const response = await Api.post(
        API_URLS.NOC.APPROVE_REJECT_ONLINE_COURSE,
        requestPayload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const nocOnlineCoursesSlice = createSlice({
  name: "nocOnlineCourses",
  initialState,
  reducers: {
    resetNocOnlineCoursesState: (state) => {
      Object.assign(state, initialState);
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentRequest: (state, action: PayloadAction<number>) => {
      const requestId = action.payload;
      const request = state.data.find((req) => req.id === requestId);
      state.currentCourse = request || null;
    },
    clearCurrentRequest: (state) => {
      state.currentCourse = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(getAllNocOnlineCourses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllNocOnlineCourses.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload.data || {};
        state.data = responseData.data || [];
        state.total = responseData.total || 0;
        state.page = responseData.page || 1;
        state.limit = responseData.limit || 10;
      })
      .addCase(getAllNocOnlineCourses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
      })
      // Fetch one
      .addCase(getNocOnlineCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNocOnlineCourse.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCourse = action.payload.data || null;
      })
      .addCase(getNocOnlineCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.currentCourse = null;
      })
      // Create or update
      .addCase(createOrUpdateNocOnlineCourse.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createOrUpdateNocOnlineCourse.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(createOrUpdateNocOnlineCourse.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteNOCOnlineCourseRequest.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteNOCOnlineCourseRequest.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.data = state.data.filter(
          (req) => req.id !== action.payload.deletedId
        );
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(deleteNOCOnlineCourseRequest.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload as string;
      })
      // Approve/Reject
      .addCase(approveRejectNocOnlineCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveRejectNocOnlineCourse.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(approveRejectNocOnlineCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch course departments
      .addCase(getCourseDepartments.pending, (state) => {
        state.courseDepartmentsLoading = true;
        state.error = null;
      })
      .addCase(getCourseDepartments.fulfilled, (state, action) => {
        state.courseDepartmentsLoading = false;
        const payloadData = Array.isArray(action.payload)
          ? action.payload
          : (Array.isArray(action.payload?.data)
            ? action.payload.data
            : (Array.isArray(action.payload?.data?.data)
              ? action.payload.data.data
              : []));
        state.courseDepartments = payloadData;
      })
      .addCase(getCourseDepartments.rejected, (state, action) => {
        state.courseDepartmentsLoading = false;
        state.error = action.payload as string;
        state.courseDepartments = [];
      });
  },
});

export const {
  resetNocOnlineCoursesState,
  clearError,
  setCurrentRequest,
  clearCurrentRequest,
} = nocOnlineCoursesSlice.actions;
export default nocOnlineCoursesSlice.reducer;
